import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, XCircle } from 'lucide-react'
import { checkPasswordRequirements, type PasswordRuleId } from './utils/passwordRequirements'
import PasswordRequirementsChecklist from './components/PasswordRequirementsChecklist'
import PasswordInput from './components/PasswordInput'

interface NewPasswordFormProps {
  onSubmit: (newPassword: string) => Promise<void>
  onCancel: () => void
}

const NewPasswordForm = ({ onSubmit, onCancel }: NewPasswordFormProps) => {
  const { t } = useTranslation(['pages', 'forms', 'errors'])
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const isMounted = useRef(true)
  useEffect(() => () => { isMounted.current = false }, [])

  const ruleLabels: Record<PasswordRuleId, string> = {
    minLength: t('pages:passwordRuleMinLength'),
    uppercase: t('pages:passwordRuleUppercase'),
    lowercase: t('pages:passwordRuleLowercase'),
    number: t('pages:passwordRuleNumber'),
    specialChar: t('pages:passwordRuleSpecialChar'),
  }

  const requirements = checkPasswordRequirements(newPassword, ruleLabels)
  const passwordsMatch = newPassword === confirmPassword
  const canSubmit = requirements.allMet && passwordsMatch && !loading

  const mapErrorMessage = (message: string): string => {
    if (message === 'Password does not meet requirements') return t('errors:passwordPolicyViolation')
    if (message === 'Your session has expired. Please sign in again.') return t('errors:challengeSessionExpired')
    return t('errors:serviceUnreachable')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    setLoading(true)
    try {
      await onSubmit(newPassword)
      // On success the component is unmounted by the auth state change —
      // do not touch any local state after this point.
    } catch (err) {
      if (isMounted.current) {
        setError(mapErrorMessage(err instanceof Error ? err.message : ''))
      }
    } finally {
      if (isMounted.current) {
        setLoading(false)
      }
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">{t('pages:newPasswordHeading')}</h2>
        <p className="text-secondary-500 mt-1">{t('pages:newPasswordSubHeading')}</p>
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm border border-red-200">
          {error}
        </div>
      )}

      <div>
        <label htmlFor="newPassword" className="block text-sm font-semibold text-gray-700 mb-1">
          {t('forms:newPassword')}
        </label>
        <PasswordInput
          id="newPassword"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          className="input"
        />
        <PasswordRequirementsChecklist rules={requirements.rules} />
      </div>

      <div>
        <label htmlFor="confirmPassword" className="block text-sm font-semibold text-gray-700 mb-1">
          {t('forms:confirmPassword')}
        </label>
        <PasswordInput
          id="confirmPassword"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          className="input"
        />
        {confirmPassword.length > 0 && (
          <p
            className={`flex items-center gap-1 mt-1 text-sm ${
              passwordsMatch ? 'text-green-700' : 'text-red-600'
            }`}
            aria-live="polite"
          >
            {passwordsMatch ? (
              <>
                <CheckCircle2 className="w-4 h-4 shrink-0" aria-hidden="true" />
                {t('pages:passwordsMatch')}
              </>
            ) : (
              <>
                <XCircle className="w-4 h-4 shrink-0" aria-hidden="true" />
                {t('pages:passwordsDoNotMatch')}
              </>
            )}
          </p>
        )}
      </div>

      <button
        type="submit"
        disabled={!canSubmit}
        className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed mt-6"
      >
        {loading ? t('pages:newPasswordButtonLoading') : t('pages:newPasswordButton')}
      </button>

      <button
        type="button"
        onClick={onCancel}
        className="w-full text-sm text-gray-500 hover:text-gray-700 mt-2"
      >
        {t('pages:cancelAction')}
      </button>
    </form>
  )
}

export default NewPasswordForm
